import React, { FC, MouseEvent, useRef, useState } from 'react'
import styled from 'styled-components'

const ImageContainer = styled.div<{ isZoomed: boolean }>`
  width: 100%;
  height: auto;
  position: relative;
  display: block;
  line-height: 0;
  overflow: visible;
  @media (min-width: 992px) {
    cursor: ${({ isZoomed }) => (isZoomed ? 'none' : 'zoom-in')};
  }
`

const Image = styled.img`
  width: 100%;
  height: auto;
  border-radius: 4px;
  object-fit: contain;
`

const MagnifyingLens = styled.div`
  position: absolute;
  z-index: 1;
  background: #e5e5e5 no-repeat;
  border: solid #ebebeb;
  box-shadow: 2px 2px 3px rgba(0, 0, 0, 0.3);
  pointer-events: none;
  border-radius: 8px;
`

interface ImageWithMagnifierProps {
  src: string
  magnifierWidth?: number
  magnifierHeight?: number
  magnifierBorderWidth?: number
  zoomFactor?: number
  alt?: string
  className?: string
}

const ImageWithMagnifier: FC<ImageWithMagnifierProps> = ({
  src,
  magnifierWidth = 450,
  magnifierHeight = 450,
  magnifierBorderWidth = 2,
  zoomFactor = 1.0,
  alt = '',
  className = '',
}) => {
  const imgRef = useRef<null | HTMLImageElement>(null)
  const [imgBounds, setImgBounds] = useState<DOMRect>()
  const [relX, setRelX] = useState(0)
  const [relY, setRelY] = useState(0)
  const [showZoom, setShowZoom] = useState(false)
  const getImgBounds = () => imgRef.current && setImgBounds(imgRef.current?.getBoundingClientRect())

  return (
    <ImageContainer
      isZoomed={showZoom}
      className={className}
      onClick={() => {
        // Only enable the zoom function on desktop
        if (window.innerWidth >= 992) {
          setShowZoom(!showZoom)
        }
      }}
      onMouseEnter={() => getImgBounds()}
      onMouseMove={(e: MouseEvent) => {
        if (imgBounds) {
          const target = e.target as HTMLElement
          setRelX((e.clientX - imgBounds.left) / target.clientWidth)
          setRelY((e.clientY - imgBounds.top) / target.clientHeight)
        }
      }}
      onMouseOut={() => {
        setShowZoom(false)
      }}
    >
      <Image ref={imgRef} src={src} alt={alt ?? ''} onLoad={() => getImgBounds()} />
      {imgBounds && showZoom && (
        <MagnifyingLens
          style={{
            width: magnifierWidth,
            height: magnifierHeight,
            borderWidth: magnifierBorderWidth,
            backgroundImage: `url("${src}")`,
            left: `calc(${relX * 100}% - ${magnifierWidth / 2}px - ${magnifierBorderWidth}px)`,
            top: `calc(${relY * 100}% - ${magnifierHeight / 2}px - ${magnifierBorderWidth}px)`,
            backgroundPosition: `
              calc(${relX * 100}% + ${magnifierWidth / 2}px - ${relX * magnifierWidth}px)
              calc(${relY * 100}% + ${magnifierHeight / 2}px - ${relY * magnifierWidth}px)
            `,
            backgroundSize: `
              ${zoomFactor * imgBounds.width}%
              auto
            `,
          }}
        />
      )}
    </ImageContainer>
  )
}

export default ImageWithMagnifier
